import {
  Box,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  Typography,
} from "@mui/material";

import { KeyboardArrowLeft } from "@mui/icons-material";
import RequestPageOutlinedIcon from "@mui/icons-material/RequestPageOutlined";
import TopicIcon from "@mui/icons-material/Topic";
import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { PagePaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { StoredCommercialPictureSearchCondition } from "../components/StoredCommercialPictureSearchCondition";
import { CommercialPictureRequestPanel } from "../components/CommercialPictureRequestPanel";
import { StoredCommercialPictureSearchPanel } from "../components/StoredCommercialPictureSearchPanel";
import * as Sentry from "@sentry/react";
import {
  searchStoredCommercialPicturesAPI,
  searchStoredCommercialRequestsAPI,
} from "../api/searchStoredCommercialPictures";
import type {
  StoredCommercialRequestSearchResponse,
  StoredCommercialPictureSearchResponse,
} from "../types";
import {
  type StoredPictureSearchFormData,
  storedPictureSearchDefaultValues,
} from "../components/storedPictureSearchConditionSchema";
import {
  loadStoredPictureSearchState,
  saveStoredPictureSearchState,
} from "../utils/storedPictureSearchState";
import {
  STORED_PICTURE_ACQUIRER_OPTIONS,
  type StoredPictureAcquirer,
} from "../utils/storedPictureSearch";
import { userDataContext } from "@/hooks/useUserData";
import { AlertBar } from "@/features/acquireMultipleBooks/components/AlertBar";
import type { HandOffMessage } from "@/types/handOffMessage";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "@/hooks/useApiClient";
import { LakersBackendApiRequestTimeoutError } from "@/utils/errors";
import { extractApiValidationMessages } from "@/utils/apiErrorMessages";

/** 検索失敗時のトースト文言 */
export const STORED_COMMERCIAL_PICTURE_SEARCH_ERROR_MESSAGE =
  "検索に失敗しました。しばらく経ってから再度お試しください。";

/** 一覧タブの種類 */
type CommercialBookListTab = "request" | "picture";

/** 取得中の行がある間、取得完了でダウンロードが押せるようになるまで一覧を更新する間隔 */
export const IN_PROGRESS_REFRESH_INTERVAL_MS = 30 * 1000;

/** 表示中の一覧を取得した検索（検索条件と取得ユーザーの組） */
interface StoredListQuery {
  condition: StoredPictureSearchFormData;
  acquirer: StoredPictureAcquirer;
}

export const CommercialBookList: React.FC = () => {
  // 遷移元の画面からデータを引き継ぐ
  const location = useLocation();
  const locationState = location.state as {
    handOffMessage?: HandOffMessage;
  } | null;
  const isAcceptanceRedirect =
    locationState?.handOffMessage?.command ===
    "COMMERCIAL_INFO_SHOW_ACCEPT_MESSAGE";
  const navigate = useNavigate();
  const { mypageTop } = useFeatureFlags();
  const { apiClient } = useApiClient();

  const { data: user } = useContext(userDataContext);
  const identity = { userId: user.id, isSharable: user.isSharable };

  // 受付直後は、保存済みの条件で新規依頼を隠さない。詳細画面から戻った場合は検索状態を復元する。
  const [restoredState] = useState(() =>
    isAcceptanceRedirect ? null : loadStoredPictureSearchState(identity)
  );

  // 表示中のタブ。復元があればそのタブ、無ければ取得依頼（親）
  const [currentTab, setCurrentTab] = useState<CommercialBookListTab>(
    restoredState?.tab ?? "request"
  );
  // 開いた直後に表示する検索。取得ユーザーは、全店で同僚の取得分を使ってもらうため「全て」から始める
  const initialQuery: StoredListQuery = {
    condition: restoredState?.condition ?? storedPictureSearchDefaultValues,
    acquirer: restoredState?.acquirer ?? "all",
  };

  const [searchResult, setSearchResult] =
    useState<StoredCommercialPictureSearchResponse>();
  // 表示中の一覧を取得した検索。取得ユーザーの選択欄もこの値を表示し、取得に失敗したら選択前の値のまま残す
  const [searchedQuery, setSearchedQuery] = useState<StoredListQuery>();
  const appliedQuery = searchedQuery ?? initialQuery;
  // 開いた直後の取得に失敗したか。結果が無いままスピナーを出し続けないよう、失敗を表示する
  const [hasLoadError, setHasLoadError] = useState(false);
  // 定期更新に失敗したとき、次の更新を予約し直すための合図
  const [refreshRetry, setRefreshRetry] = useState(0);
  // 定期更新の連続失敗回数。失敗が続く間に同じ障害をSentryへ送り続けないよう、1回目だけ送る
  const consecutiveRefreshFailures = useRef(0);
  const latestSearch = useRef(0);
  const pictureResults = searchResult?.results;
  const [requestResult, setRequestResult] =
    useState<StoredCommercialRequestSearchResponse>();
  const activeResult = currentTab === "request" ? requestResult : searchResult;
  const [isSearching, setIsSearching] = useState<boolean>(false);

  // メッセージを表示するためのState、 ""の場合表示しない
  const [alertMessage, setAlertMessage] = useState(
    isAcceptanceRedirect
      ? "商業登記取得を受け付けました。取得状況は一覧から確認できます。"
      : ""
  );

  useEffect(() => {
    if (!isAcceptanceRedirect) return;
    // 履歴に残すと、詳細から戻るたびに検索状態まで初期化してしまう。
    navigate(".", {
      replace: true,
      state: { ...locationState, handOffMessage: undefined },
    });
  }, [isAcceptanceRedirect, locationState, navigate]);

  /**
   * 検索条件と取得ユーザーを表示対象タブの専用APIへ送り、サーバ側でページングした結果を表示する。
   * 成功したら、詳細画面から戻った際に復元できるよう検索状態を保存する。
   * 失敗時はトースト（400ならBEのバリデーションメッセージ）を出し、表示中の内容は保持する
   * @param query 検索条件と取得ユーザー
   * @param page ページ番号（1始まり）
   * @param pageSize 1ページの件数
   * @param tab 表示対象のタブ
   * @param silent 取得中の行の定期更新か。true ならローディング表示・トースト・保存をしない
   */
  const fetchPage = (
    query: StoredListQuery,
    page = 1,
    pageSize = 50,
    tab = currentTab,
    silent = false
  ): void => {
    const searchId = ++latestSearch.current;
    // 共有OFFの会社は選択欄を出さないため、取得ユーザーで絞らない（APIも本人分だけを返す）
    const effectiveAcquirer = user.isSharable ? query.acquirer : "all";
    void (async () => {
      if (!silent) setIsSearching(true);
      try {
        if (tab === "request") {
          const response = await searchStoredCommercialRequestsAPI(
            apiClient,
            query.condition,
            page,
            pageSize,
            effectiveAcquirer
          );
          if (searchId !== latestSearch.current) return;
          setRequestResult(response);
        } else {
          const response = await searchStoredCommercialPicturesAPI(
            apiClient,
            query.condition,
            page,
            pageSize,
            effectiveAcquirer
          );
          if (searchId !== latestSearch.current) return;
          setSearchResult(response);
        }
        setCurrentTab(tab);
        setSearchedQuery(query);
        setHasLoadError(false);
        consecutiveRefreshFailures.current = 0;
        if (!silent)
          saveStoredPictureSearchState(
            query.condition,
            tab,
            query.acquirer,
            identity
          );
      } catch (error) {
        if (searchId !== latestSearch.current) return;
        if (silent) {
          consecutiveRefreshFailures.current += 1;
          if (
            consecutiveRefreshFailures.current === 1 &&
            !(error instanceof LakersBackendApiRequestTimeoutError) &&
            extractApiValidationMessages(error).length === 0
          )
            Sentry.captureException(error);
          setRefreshRetry((count) => count + 1);
          return;
        }
        setHasLoadError(true);
        if (error instanceof LakersBackendApiRequestTimeoutError) {
          toast.info(error.message);
          return;
        }
        const validationMessages = extractApiValidationMessages(error);
        if (validationMessages.length > 0) {
          toast.error(validationMessages.join("\n"));
          return;
        }
        // タイムアウト・入力バリデーション以外の想定外エラー（500系等）は本番検知のためSentryへ送る
        Sentry.captureException(error);
        toast.error(STORED_COMMERCIAL_PICTURE_SEARCH_ERROR_MESSAGE);
      } finally {
        if (searchId === latestSearch.current && !silent) setIsSearching(false);
      }
    })();
  };

  /**
   * 検索ボタン押下時の処理。条件なしの検索は、取得ユーザーの範囲の全件を表示する。
   * @param condition 検索条件
   */
  const handleSearch = (condition: StoredPictureSearchFormData): void => {
    fetchPage(
      { condition, acquirer: appliedQuery.acquirer },
      1,
      activeResult?.pageSize ?? 50
    );
  };

  // マウント時に一覧を表示する。詳細画面から戻ってきた場合は同じ条件・タブ・取得ユーザーで取得し直す
  // （署名付きURL等が期限切れにならないよう、保存結果を使い回さない）
  useEffect(() => {
    fetchPage(initialQuery, 1, 50, currentTab);
    // マウント時に一度だけ実行する（restoredState はマウント時に確定した値）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 取得中の行がある間だけ、同じページを定期的に取得し直す（取得完了でダウンロードが押せるようになるため）
  const hasInProgressRow =
    currentTab === "request"
      ? requestResult?.results.some((row) => row.status === "REQUESTING")
      : searchResult?.results.some(
          (row) => row.status === "REQUESTING" || row.status === "NEEDS_REVIEW"
        );
  useEffect(() => {
    if (
      !hasInProgressRow ||
      searchedQuery === undefined ||
      activeResult === undefined ||
      isSearching
    )
      return;
    const timer = setTimeout(() => {
      fetchPage(
        searchedQuery,
        activeResult.page,
        activeResult.pageSize,
        currentTab,
        true
      );
    }, IN_PROGRESS_REFRESH_INTERVAL_MS);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasInProgressRow,
    searchedQuery,
    activeResult,
    currentTab,
    isSearching,
    refreshRetry,
  ]);

  /** クリア押下時は、検索条件を外して取得ユーザーの範囲の全件を表示し直す */
  const handleClear = (): void => {
    fetchPage(
      {
        condition: storedPictureSearchDefaultValues,
        acquirer: appliedQuery.acquirer,
      },
      1,
      activeResult?.pageSize ?? 50
    );
  };

  return (
    <PagePaper>
      {alertMessage !== "" ? (
        <AlertBar
          severity="info"
          message={alertMessage}
          onClose={() => {
            setAlertMessage("");
          }}
        />
      ) : (
        ""
      )}
      {/* タイトルと新規取得導線を1行に置き、導線はリンクにして主張を抑える */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TopicIcon sx={{ mr: 1 }} fontSize="large" />
          <PageTitle>取得済商業登記を検索する</PageTitle>
        </Box>
        <Link
          component={RouterLink}
          to={"/commercialbook"}
          variant="body2"
          color="text.secondary"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <RequestPageOutlinedIcon fontSize="inherit" aria-hidden />
          商業登記簿を取得する
        </Link>
      </Box>

      {/* 検索条件はタブより上に置き、両タブ共通で結果を絞り込む */}
      <StoredCommercialPictureSearchCondition
        handleSearch={handleSearch}
        handleClear={handleClear}
        isLoading={isSearching}
        initialValues={restoredState?.condition}
      />

      <Box sx={{ mt: 3 }} />

      {/* 同じ検索結果を、取得依頼（親）単位／登記明細（子）単位のどちらで見るかをタブで切り替える */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(_event, value: CommercialBookListTab) => {
            fetchPage(appliedQuery, 1, activeResult?.pageSize ?? 50, value);
          }}
          aria-label="取得済商業登記の表示単位タブ"
        >
          <Tab
            label="取得依頼から探す"
            value="request"
            disabled={isSearching}
          />
          <Tab label="登記から探す" value="picture" disabled={isSearching} />
        </Tabs>
      </Box>

      {/* タブごとに表示単位が異なることを補足する（PCではタブから1段下げて余白を持たせる） */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2,
          mt: { xs: 0, md: 2 },
          fontSize: { xs: "0.75rem", sm: "0.875rem" },
          lineHeight: 1.5,
        }}
      >
        {currentTab === "request"
          ? "取得依頼（複数社をまとめて依頼した単位）ごとに表示します。検索すると、条件に合う登記を含む依頼だけに絞り込みます。「詳細」からその依頼の登記を確認できます。"
          : "登記（1社1登記）ごとに表示します。上の検索条件で絞り込みます。「詳細」からその登記を含む取得依頼の詳細を確認できます。"}
      </Typography>

      {user.isSharable && (
        <Stack direction="row" sx={{ mb: 2 }}>
          <Select
            inputProps={{ "aria-label": "取得ユーザー" }}
            size="small"
            value={appliedQuery.acquirer}
            disabled={isSearching}
            onChange={(event) => {
              fetchPage(
                {
                  condition: appliedQuery.condition,
                  acquirer: event.target.value as StoredPictureAcquirer,
                },
                1,
                activeResult?.pageSize ?? 50
              );
            }}
            sx={{ minWidth: 180 }}
          >
            {STORED_PICTURE_ACQUIRER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      {currentTab === "request" ? (
        <CommercialPictureRequestPanel
          results={requestResult?.results}
          loading={isSearching}
          loadFailed={hasLoadError}
          isSharable={user.isSharable}
        />
      ) : (
        <StoredCommercialPictureSearchPanel
          results={pictureResults}
          loading={isSearching}
          loadFailed={hasLoadError}
          isSharable={user.isSharable}
        />
      )}
      {activeResult !== undefined && (
        <TablePagination
          component="div"
          count={activeResult.count}
          page={activeResult.page - 1}
          rowsPerPage={activeResult.pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="ページあたりの行数:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}～${to} / ${count}`
          }
          backIconButtonProps={{
            disabled: isSearching || activeResult.page <= 1,
          }}
          nextIconButtonProps={{
            disabled:
              isSearching ||
              activeResult.page * activeResult.pageSize >= activeResult.count,
          }}
          SelectProps={{ disabled: isSearching }}
          onPageChange={(_event, page) => {
            if (searchedQuery !== undefined)
              fetchPage(searchedQuery, page + 1, activeResult.pageSize);
          }}
          onRowsPerPageChange={(event) => {
            if (searchedQuery !== undefined)
              fetchPage(searchedQuery, 1, Number(event.target.value));
          }}
        />
      )}

      {/* 戻る */}
      <Box sx={{ mt: 2 }}>
        <Link
          component={RouterLink}
          to={mypageTop ? "/mypage" : "/feed"}
          sx={{ display: "inline-block" }}
        >
          <Box sx={{ display: "flex" }}>
            <KeyboardArrowLeft />
            <Typography>
              {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
            </Typography>
          </Box>
        </Link>
      </Box>
    </PagePaper>
  );
};
